/**
 * @file firebase.ts
 * @description Configuración e inicialización de Firebase
 * @author XiaVan Team
 */

import { initializeApp } from 'firebase/app'
import { getAuth, GoogleAuthProvider } from 'firebase/auth'
import { getFirestore } from 'firebase/firestore'
import { getStorage } from 'firebase/storage'
import { getAnalytics } from 'firebase/analytics'

/**
 * Configuración de Firebase
 * Las credenciales se cargan desde variables de entorno
 * para mantener la seguridad (no exponer claves en código)
 */
const firebaseConfig = {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
  authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
  storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID,
  appId: import.meta.env.VITE_FIREBASE_APP_ID,
  measurementId: import.meta.env.VITE_FIREBASE_MEASUREMENT_ID,
}

/**
 * Inicializar Firebase App
 */
const app = initializeApp(firebaseConfig)

/**
 * Servicios de Firebase
 */

// Authentication - Para login con Google OAuth
export const auth = getAuth(app)

// Google Auth Provider - Proveedor de autenticación de Google
export const googleProvider = new GoogleAuthProvider()
googleProvider.setCustomParameters({
  prompt: 'select_account', // Always show account selection
})

// Firestore Database - Base de datos NoSQL
export const db = getFirestore(app)

// Storage - Almacenamiento de archivos (imágenes, etc.)
export const storage = getStorage(app)

// Analytics - Métricas de uso (opcional)
let analytics: ReturnType<typeof getAnalytics> | null = null
if (typeof window !== 'undefined') {
  analytics = getAnalytics(app)
}
export { analytics }

/**
 * Lista blanca de emails autorizados
 * Solo estos usuarios pueden acceder a la app
 * 
 * NOTE: Esta lista está duplicada en Firestore (allowedEmails collection)
 * Se mantiene aquí para validación rápida en el cliente
 */
export const ALLOWED_EMAILS = [
  'asierazconalopez@gmail.com',
  'lauramontero84@gmail.com',
  'x14v4n@gmail.com',
  'xiaazconamontero@gmail.com',
]

/**
 * Verificar si un email está autorizado
 * @param email - Email a verificar
 * @returns true si está en la lista blanca
 */
export function isEmailAllowed(email: string | null | undefined): boolean {
  if (!email) return false
  return ALLOWED_EMAILS.includes(email.toLowerCase())
}

export default app
